import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { FilterBar } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { SourcesMenu } from '@/features/SourcesMenu';
import { QueryAttachmentRow } from './QueryAttachmentRow';
import { Actions } from './Actions';
import { deriveAttachmentDisplay } from '../utils/attachmentDisplay';
import type { QueryPanelState } from '../hooks';

interface QueryPanelBodyProps {
    state: QueryPanelState;
    inputSlot: React.ReactNode;
    actionsWrapper?: (actions: React.ReactNode) => React.ReactNode;
    filtersWrapper?: (filters: React.ReactNode) => React.ReactNode;
    filterTrailingSlot?: React.ReactNode;
    actionsTrailingSlot?: React.ReactNode;
}

export function QueryPanelBody({
    state,
    inputSlot,
    actionsWrapper,
    filtersWrapper,
    filterTrailingSlot,
    actionsTrailingSlot,
}: QueryPanelBodyProps) {
    const { t } = useTranslation(I18nNamespace.QUERIES);
    const {
        imageSrc: attachmentImageSrc,
        label: attachmentLabel,
        showRow: showAttachmentRow,
    } = deriveAttachmentDisplay({
        attachmentFile: state.attachmentFile,
        uploadPreviewUrl: state.uploadPreviewUrl,
        uploadingFileName: state.uploadingFileName,
    });

    const sourcesMenuContent = (
        <SourcesMenu
            onClose={() => state.setSourcesListOpen(false)}
            selectionOverride={{
                selected: state.draft.draftSources.selected,
                onToggle: state.draft.toggleSource,
                onSelect: state.draft.selectSource,
                onDeselect: state.draft.deselectSource,
                onSelectMany: state.draft.selectSources,
                onDeselectMany: state.draft.deselectSources,
                onSelectFolder: state.draft.selectFolderSources,
            }}
        />
    );

    const filtersContent = (
        <>
            {showAttachmentRow && attachmentImageSrc && (
                <QueryAttachmentRow
                    imageSrc={attachmentImageSrc}
                    fileName={attachmentLabel}
                    isUploading={state.isUploading}
                    uploadProgress={state.uploadProgress}
                    onRemove={state.handleRemove}
                />
            )}
            <FilterBar
                className={cn(
                    'pl-2 pr-[14px]',
                    showAttachmentRow ? 'pt-1' : 'pt-0',
                )}
                onDateTimeFilterApply={
                    state.isVideoSourceSelected
                        ? undefined
                        : state.handleDateTimeFilterApply
                }
                onConfidenceFilterApply={state.handleConfidenceFilterApply}
                draftOverride={{
                    sources: state.draft.draftSources,
                    filters: state.draft.draftFilters,
                    queryPanelAllSourcesLabel: t(
                        'sourcesChip.noSourcesSelected',
                    ),
                    sourcesPopover: {
                        open: state.sourcesListOpen,
                        onOpenChange: state.setSourcesListOpen,
                        content: sourcesMenuContent,
                    },
                    sourcesInvalid: state.sourcesInvalid,
                }}
            />
        </>
    );

    const actionsContent = <Actions state={state} />;

    const wrappedFilters = filtersWrapper
        ? filtersWrapper(filtersContent)
        : filtersContent;

    const wrappedActions = actionsWrapper
        ? actionsWrapper(actionsContent)
        : actionsContent;

    return (
        <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center">
                <div className="min-w-0 flex-1 py-2">{wrappedFilters}</div>
                {filterTrailingSlot && (
                    <div className="shrink-0 px-3.5">{filterTrailingSlot}</div>
                )}
            </div>
            <div className="px-3.5 py-4">{inputSlot}</div>
            <div className="flex items-center p-3.5">
                <div className="min-w-0 flex-1">{wrappedActions}</div>
                {actionsTrailingSlot && (
                    <div className="shrink-0">{actionsTrailingSlot}</div>
                )}
            </div>
        </div>
    );
}
